/**
 * Energy & Utilities Industry Configuration
 *
 * Supports: Transformers, Generators, Turbines, Solar Panels, Wind Turbines, Grid Infrastructure
 */

import { IndustryConfig } from '../industryTypes'

export const energyConfig: IndustryConfig = {
  id: 'energy',
  name: 'Energy & Utilities',
  description: 'Power generation, transmission, and distribution',
  icon: 'Zap',

  terminology: {
    asset: 'Asset',
    assets: 'Assets',
    assetPlural: 'Assets',
    assetType: 'Asset Type',
    failure: 'Outage',
    health: 'Health',
    healthScore: 'Health Score',
    healthy: 'Online',
    degraded: 'Degraded',
    critical: 'Offline',
    alert: 'Alert',
    process: 'Generation',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Downtime',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Station',
    facilityPlural: 'Stations',
    location: 'Zone',
    operator: 'Technician',
    supervisor: 'Station Manager',
    calibration: 'Calibration',
    compliance: 'Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'transformer',
      name: 'Power Transformer',
      icon: 'Boxes',
      description: 'High voltage power transformers',
      category: 'Transmission',
      defaultMetrics: ['oil_temperature', 'winding_temperature', 'dissolved_gas', 'load_factor'],
      maintenanceTypes: ['oil_analysis', 'thermography', 'bushing_test', 'major_overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Single point of failure', downtimeCostMultiplier: 500 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Major load center', downtimeCostMultiplier: 200 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Redundant path available', downtimeCostMultiplier: 50 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Distribution level', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'generator',
      name: 'Generator',
      icon: 'CircleDot',
      description: 'Synchronous generators',
      category: 'Generation',
      defaultMetrics: ['power_output', 'frequency', 'vibration', 'bearing_temperature'],
      maintenanceTypes: ['stator_inspection', 'rotor_inspection', 'exciter_service', 'major_overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Baseload', color: '#ef4444', description: 'Baseload generation', downtimeCostMultiplier: 1000 },
        { id: 'high', name: 'Peaking', color: '#f59e0b', description: 'Peak demand unit', downtimeCostMultiplier: 300 },
        { id: 'medium', name: 'Reserve', color: '#3b82f6', description: 'Reserve capacity', downtimeCostMultiplier: 100 },
        { id: 'low', name: 'Auxiliary', color: '#6b7280', description: 'Auxiliary generation', downtimeCostMultiplier: 20 },
      ],
    },
    {
      id: 'wind_turbine',
      name: 'Wind Turbine',
      icon: 'Fan',
      description: 'Utility-scale wind turbines',
      category: 'Renewable',
      defaultMetrics: ['power_output', 'rotor_speed', 'gearbox_temperature', 'pitch_angle'],
      maintenanceTypes: ['blade_inspection', 'gearbox_oil_change', 'generator_service', 'foundation_check'],
      criticalityLevels: [
        { id: 'critical', name: 'High Output', color: '#ef4444', description: 'High capacity factor unit', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'Standard', color: '#f59e0b', description: 'Standard turbine', downtimeCostMultiplier: 50 },
        { id: 'medium', name: 'Older', color: '#3b82f6', description: 'Older turbine model', downtimeCostMultiplier: 20 },
        { id: 'low', name: 'Test', color: '#6b7280', description: 'Test/prototype unit', downtimeCostMultiplier: 5 },
      ],
    },
    {
      id: 'solar_array',
      name: 'Solar Array',
      icon: 'Sun',
      description: 'Photovoltaic solar arrays',
      category: 'Renewable',
      defaultMetrics: ['power_output', 'irradiance', 'temperature', 'inverter_efficiency'],
      maintenanceTypes: ['panel_cleaning', 'inverter_service', 'wiring_inspection', 'performance_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Utility Scale', color: '#ef4444', description: 'Large utility installation', downtimeCostMultiplier: 200 },
        { id: 'high', name: 'Commercial', color: '#f59e0b', description: 'Commercial installation', downtimeCostMultiplier: 50 },
        { id: 'medium', name: 'Distributed', color: '#3b82f6', description: 'Distributed generation', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Residential', color: '#6b7280', description: 'Residential scale', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'battery_storage',
      name: 'Battery Storage',
      icon: 'Battery',
      description: 'Grid-scale battery storage systems',
      category: 'Storage',
      defaultMetrics: ['state_of_charge', 'temperature', 'cycle_count', 'capacity_fade'],
      maintenanceTypes: ['thermal_management', 'bms_calibration', 'connection_inspection', 'capacity_test'],
      criticalityLevels: [
        { id: 'critical', name: 'Grid Services', color: '#ef4444', description: 'Frequency regulation', downtimeCostMultiplier: 300 },
        { id: 'high', name: 'Peak Shaving', color: '#f59e0b', description: 'Peak demand reduction', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Time Shift', color: '#3b82f6', description: 'Energy arbitrage', downtimeCostMultiplier: 30 },
        { id: 'low', name: 'Backup', color: '#6b7280', description: 'Backup power', downtimeCostMultiplier: 10 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'generation_efficiency',
      name: 'Generation Efficiency',
      icon: 'Gauge',
      description: 'Power generation efficiency metrics',
      businessValue: 'Maximize energy output and minimize losses',
      category: 'universal',
      metrics: [
        { id: 'heat_rate', name: 'Heat Rate', unit: 'BTU/kWh', description: 'Fuel efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 9000, warning: 10000, critical: 11000 } },
        { id: 'capacity_factor', name: 'Capacity Factor', unit: '%', description: 'Actual vs nameplate output', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 55 } },
        { id: 'availability', name: 'Availability', unit: '%', description: 'Available for generation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 80 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'grid_stability',
      name: 'Grid Stability',
      icon: 'Activity',
      description: 'Power quality and grid stability metrics',
      businessValue: 'Ensure reliable power delivery',
      category: 'industry_specific',
      metrics: [
        { id: 'frequency', name: 'Grid Frequency', unit: 'Hz', description: 'System frequency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 60.0, warning: 59.95, critical: 59.90 } },
        { id: 'voltage', name: 'Voltage', unit: 'kV', description: 'Bus voltage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'power_factor', name: 'Power Factor', unit: '', description: 'Real vs apparent power', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 0.95, warning: 0.90, critical: 0.85 } },
      ],
      thresholds: { healthy: { min: 59.98 }, warning: { min: 59.95, max: 59.98 }, critical: { max: 59.95 } },
      visualizations: ['line_chart', 'gauge', 'distribution', 'trend'],
    },
    {
      id: 'emissions',
      name: 'Emissions Monitoring',
      icon: 'Cloud',
      description: 'Environmental emissions tracking',
      businessValue: 'Meet emissions targets and regulations',
      category: 'industry_specific',
      metrics: [
        { id: 'co2_intensity', name: 'CO2 Intensity', unit: 'kg/MWh', description: 'Carbon per energy', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 400, warning: 600, critical: 800 } },
        { id: 'nox', name: 'NOx Emissions', unit: 'lb/MWh', description: 'Nitrogen oxides', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 1.0, critical: 1.5 } },
        { id: 'so2', name: 'SO2 Emissions', unit: 'lb/MWh', description: 'Sulfur dioxide', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.3, warning: 0.5, critical: 1.0 } },
      ],
      thresholds: { healthy: { max: 400 }, warning: { min: 400, max: 700 }, critical: { min: 700 } },
      visualizations: ['line_chart', 'bar_chart', 'comparison', 'trend'],
    },
    {
      id: 'renewable_performance',
      name: 'Renewable Performance',
      icon: 'Leaf',
      description: 'Renewable energy generation metrics',
      businessValue: 'Optimize renewable asset performance',
      category: 'industry_specific',
      metrics: [
        { id: 'performance_ratio', name: 'Performance Ratio', unit: '%', description: 'Actual vs theoretical output', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 75, critical: 65 } },
        { id: 'specific_yield', name: 'Specific Yield', unit: 'kWh/kWp', description: 'Energy per rated capacity', dataType: 'continuous', aggregation: 'sum', goodDirection: 'higher', defaultThresholds: { healthy: 1500, warning: 1200, critical: 900 } },
        { id: 'curtailment', name: 'Curtailment', unit: '%', description: 'Lost generation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['line_chart', 'bar_chart', 'heatmap', 'comparison'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'oil_analysis',
      name: 'Transformer Oil Analysis',
      description: 'Dissolved gas analysis and oil testing',
      category: 'condition_based',
      defaultInterval: { value: 6, unit: 'months' },
      laborRequirements: [
        { role: 'Electrical Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['sampling', 'electrical_safety'],
      partsCommonly: ['oil_sample_kits', 'ppe'],
    },
    {
      id: 'turbine_inspection',
      name: 'Turbine Inspection',
      description: 'Steam/gas turbine inspection',
      category: 'preventive',
      defaultInterval: { value: 18, unit: 'months' },
      laborRequirements: [
        { role: 'Turbine Specialist', count: 2, hourlyRate: 85 },
        { role: 'Engineer', count: 1, hourlyRate: 100 },
      ],
      estimatedDuration: { min: 3, typical: 5, max: 10, unit: 'days' },
      skillsRequired: ['turbine_mechanics', 'ndt_inspection'],
      partsCommonly: ['blades', 'seals', 'bearings'],
    },
    {
      id: 'blade_inspection',
      name: 'Wind Turbine Blade Inspection',
      description: 'Drone-based blade inspection',
      category: 'condition_based',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Drone Operator', count: 1, hourlyRate: 65 },
        { role: 'Blade Inspector', count: 1, hourlyRate: 75 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['drone_operation', 'blade_analysis'],
      partsCommonly: ['drone', 'inspection_software'],
    },
  ],

  riskFactors: [
    { id: 'outage_risk', name: 'Outage Risk', description: 'Risk of unplanned outage', category: 'operational', weight: 0.35, calculationMethod: 'exponential', inputs: ['health_score', 'age', 'load_factor'] },
    { id: 'environmental_risk', name: 'Environmental Risk', description: 'Risk of environmental incidents', category: 'environmental', weight: 0.25, calculationMethod: 'linear', inputs: ['oil_condition', 'leak_detection'] },
    { id: 'regulatory_risk', name: 'Regulatory Risk', description: 'Risk of compliance violation', category: 'compliance', weight: 0.20, calculationMethod: 'threshold', inputs: ['emissions', 'permit_status'] },
    { id: 'financial_risk', name: 'Market Risk', description: 'Risk from price volatility', category: 'financial', weight: 0.20, calculationMethod: 'linear', inputs: ['capacity_factor', 'market_price'] },
  ],

  complianceStandards: [
    {
      id: 'nerc',
      name: 'NERC Reliability Standards',
      shortName: 'NERC',
      description: 'North American grid reliability standards',
      authority: 'NERC',
      region: ['US', 'Canada'],
      requirements: [
        { id: 'fac_001', name: 'Facility Ratings', description: 'Accurate facility ratings', kpiId: 'facility_ratings_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'prc_005', name: 'Protection Systems', description: 'Protection system maintenance', kpiId: 'protection_test_current', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'epa_mats',
      name: 'EPA MATS Rule',
      shortName: 'MATS',
      description: 'Mercury and Air Toxics Standards',
      authority: 'EPA',
      region: ['US'],
      requirements: [
        { id: 'mercury', name: 'Mercury Limits', description: 'Mercury emission limits', kpiId: 'mercury_emissions', threshold: 0.0002, operator: 'lte', severity: 'critical' },
        { id: 'hcl', name: 'HCl Limits', description: 'Hydrogen chloride limits', kpiId: 'hcl_emissions', threshold: 0.002, operator: 'lte', severity: 'critical' },
      ],
      reportingFrequency: 'quarterly',
    },
  ],

  visualization: {
    primaryColor: '#eab308',
    secondaryColor: '#22c55e',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Asset Health Summary',
      context: 'asset_health',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Your {assetType} "{assetName}" at {facility} is currently {healthStatus}. {trendDescription} Current output: {output}MW ({capacityFactor}% capacity factor). {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'facility', type: 'terminology' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'output', type: 'metric' },
        { name: 'capacityFactor', type: 'percentage' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 30,
    defaultDashboard: 'command-center',
  },

  // Professional Roles
  professionalRoles: [
    // SME Roles
    {
      id: 'power_engineer',
      category: 'sme',
      name: 'Power Systems Engineer',
      shortName: 'PSE',
      description: 'Designs, analyzes, and optimizes power generation, transmission, and distribution systems for reliability and efficiency',
      responsibilities: [
        'Power system modeling and analysis',
        'Grid stability and protection coordination',
        'Generation dispatch optimization',
        'Outage root cause analysis',
        'Capital project engineering support',
      ],
      focusAreas: ['generation_efficiency', 'grid_reliability', 'nerc_compliance', 'protection_systems', 'power_quality'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'compliance', 'action_items'],
      },
      decisionAuthority: ['generation_scheduling', 'equipment_dispatch', 'protection_settings', 'equipment_modifications'],
      reportingTo: 'Engineering Manager',
      icon: 'Zap',
    },
    // Executive / Operations Roles
    {
      id: 'grid_operations_manager',
      category: 'executive',
      name: 'Grid Operations Manager',
      shortName: 'GOM',
      description: 'Oversees real-time grid operations, load balancing, and dispatch to ensure continuous reliable power delivery',
      responsibilities: [
        'Real-time grid monitoring and control',
        'Generation dispatch and load balancing',
        'Outage coordination and restoration',
        'Interconnection management',
        'Emergency response coordination',
      ],
      focusAreas: ['grid_stability', 'load_forecasting', 'outage_management', 'dispatch_optimization', 'emergency_response'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['business_impact', 'action_items', 'risk'],
      },
      decisionAuthority: ['real_time_dispatch', 'outage_restoration_priority', 'load_shedding', 'emergency_declarations'],
      reportingTo: 'VP Operations',
      icon: 'Activity',
    },
    // Technician Roles
    {
      id: 'generation_technician',
      category: 'technician',
      name: 'Generation Technician',
      shortName: 'GT',
      description: 'Performs hands-on maintenance, troubleshooting, and repair of power generation equipment including turbines, generators, and auxiliaries',
      responsibilities: [
        'Turbine and generator maintenance execution',
        'Equipment troubleshooting and repair',
        'Preventive maintenance rounds',
        'Oil sampling and condition monitoring',
        'Work order completion and documentation',
      ],
      focusAreas: ['fault_codes', 'repair_procedures', 'parts_needed', 'safety_requirements', 'lockout_tagout'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['repair_methods', 'parts_selection', 'immediate_safety_stops', 'equipment_isolation'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    // Analyst Roles
    {
      id: 'renewable_energy_analyst',
      category: 'analyst',
      name: 'Renewable Energy Analyst',
      shortName: 'REA',
      description: 'Analyzes performance data from wind, solar, and battery storage assets to optimize output and identify degradation trends',
      responsibilities: [
        'Renewable asset performance reporting',
        'Energy yield analysis and forecasting',
        'Curtailment and availability tracking',
        'Degradation trend identification',
        'Benchmarking across fleet assets',
      ],
      focusAreas: ['performance_ratio', 'capacity_factor', 'curtailment', 'degradation_rates', 'weather_correlation'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['report_formats', 'data_collection_methods', 'analysis_priorities', 'performance_benchmarks'],
      reportingTo: 'Asset Management Director',
      icon: 'BarChart',
    },
    // Compliance / SME Roles
    {
      id: 'safety_compliance_officer',
      category: 'sme',
      name: 'Safety Compliance Officer',
      shortName: 'SCO',
      description: 'Ensures regulatory compliance with NERC, EPA, OSHA, and other standards governing power generation and transmission operations',
      responsibilities: [
        'NERC reliability standards compliance',
        'Environmental permit and emissions monitoring',
        'Safety program management and audits',
        'Incident investigation and reporting',
        'Regulatory filing and documentation',
      ],
      focusAreas: ['nerc_compliance', 'epa_emissions', 'osha_safety', 'audit_readiness', 'incident_rates'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['compliance', 'risk', 'business_impact'],
      },
      decisionAuthority: ['compliance_actions', 'safety_stops', 'regulatory_filings', 'corrective_action_plans'],
      reportingTo: 'VP Compliance',
      icon: 'Shield',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Energy Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'transformer_parts',
        name: 'Transformer Components',
        description: 'Bushings, insulation, tap changers, and cooling components for power transformers',
        parts: [
          {
            id: 'xfmr_bushing_145kv',
            partNumber: 'ABB-GOB-145',
            name: 'Transformer Bushing 145kV',
            description: 'Oil-impregnated condenser bushing for 145kV class transformers',
            category: 'transformer_parts',
            manufacturer: 'ABB',
            unitCost: 18500,
            currency: 'USD',
            leadTime: { value: 12, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['transformer'],
            failureModes: ['bushing_flashover', 'oil_leak', 'insulation_breakdown'],
            averageLifespan: { value: 30, unit: 'years' },
            specifications: { voltageClass: '145kV', bilRating: '650kV', currentRating: '2000A' },
          },
          {
            id: 'xfmr_winding_insulation',
            partNumber: 'DUPONT-NOMEX410',
            name: 'Winding Insulation Paper (Nomex 410)',
            description: 'Aramid insulation paper for transformer winding re-insulation, 0.18mm thickness',
            category: 'transformer_parts',
            manufacturer: 'DuPont',
            unitCost: 850,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['transformer'],
            failureModes: ['insulation_breakdown', 'thermal_degradation'],
            specifications: { thickness: '0.18mm', thermalClass: '220°C', dielectricStrength: '20kV/mm' },
          },
          {
            id: 'xfmr_tap_changer_contacts',
            partNumber: 'MR-OILTAP-M',
            name: 'On-Load Tap Changer Contact Set',
            description: 'Complete contact set for MR on-load tap changer, includes main and arcing contacts',
            category: 'transformer_parts',
            manufacturer: 'Reinhausen',
            unitCost: 4200,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['transformer'],
            failureModes: ['contact_erosion', 'tap_changer_failure', 'voltage_regulation_loss'],
            averageLifespan: { value: 100000, unit: 'cycles' },
            specifications: { type: 'On-load', contactMaterial: 'Tungsten-copper', maxCurrent: '1600A' },
          },
        ],
      },
      {
        id: 'turbine_parts',
        name: 'Turbine Components',
        description: 'Blades, bearings, fuel nozzles, and seals for gas and steam turbines',
        parts: [
          {
            id: 'gt_blade_row1',
            partNumber: 'GE-7FA-BLD-R1',
            name: 'Gas Turbine First-Stage Blade',
            description: 'Single-crystal nickel superalloy blade with thermal barrier coating for first stage',
            category: 'turbine_parts',
            manufacturer: 'GE Power',
            unitCost: 28000,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['generator'],
            failureModes: ['blade_erosion', 'thermal_fatigue', 'coating_spallation', 'foreign_object_damage'],
            averageLifespan: { value: 48000, unit: 'hours' },
            specifications: { material: 'Single-crystal superalloy', coating: 'TBC', stagePosition: 'Row 1' },
          },
          {
            id: 'gt_bearing_journal',
            partNumber: 'GE-H65-JB',
            name: 'Turbine Journal Bearing',
            description: 'Tilting-pad journal bearing for gas turbine rotor support',
            category: 'turbine_parts',
            manufacturer: 'GE Power',
            unitCost: 15000,
            currency: 'USD',
            leadTime: { value: 10, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['generator'],
            failureModes: ['bearing_wear', 'babbitt_wipe', 'oil_whirl'],
            averageLifespan: { value: 60000, unit: 'hours' },
            specifications: { type: 'Tilting-pad', material: 'Babbitt-lined', loadCapacity: '50000 lbs' },
          },
          {
            id: 'gt_fuel_nozzle',
            partNumber: 'GE-DLN2.6-FN',
            name: 'Dry Low-NOx Fuel Nozzle',
            description: 'Premixed fuel nozzle assembly for DLN 2.6 combustion system',
            category: 'turbine_parts',
            manufacturer: 'GE Power',
            unitCost: 35000,
            currency: 'USD',
            leadTime: { value: 14, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['generator'],
            failureModes: ['nozzle_erosion', 'fuel_leak', 'combustion_dynamics'],
            averageLifespan: { value: 24000, unit: 'hours' },
            specifications: { type: 'DLN 2.6 Premixed', fuelType: 'Natural gas', noxTarget: '<9 ppm' },
          },
        ],
      },
      {
        id: 'generator_parts',
        name: 'Generator Components',
        description: 'Brushes, stator windings, exciter components, and cooling system parts for synchronous generators',
        parts: [
          {
            id: 'gen_carbon_brush',
            partNumber: 'MERSEN-CG665',
            name: 'Generator Carbon Brush Set',
            description: 'Electrographite carbon brush set for synchronous generator slip rings, set of 4',
            category: 'generator_parts',
            manufacturer: 'Mersen',
            unitCost: 320,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['generator'],
            failureModes: ['brush_wear', 'sparking', 'ring_damage'],
            averageLifespan: { value: 6000, unit: 'hours' },
            specifications: { grade: 'CG665', quantity: '4 per set', current: '200A per brush' },
          },
          {
            id: 'gen_stator_winding_bar',
            partNumber: 'GE-STBAR-18KV',
            name: 'Stator Winding Bar',
            description: 'Roebel-transposed stator bar with VPI insulation for 18kV generator',
            category: 'generator_parts',
            manufacturer: 'GE Power',
            unitCost: 12000,
            currency: 'USD',
            leadTime: { value: 20, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['generator'],
            failureModes: ['insulation_breakdown', 'partial_discharge', 'end_winding_vibration'],
            averageLifespan: { value: 30, unit: 'years' },
            specifications: { voltage: '18kV', insulation: 'VPI Epoxy-Mica', construction: 'Roebel-transposed' },
          },
          {
            id: 'gen_hydrogen_seal',
            partNumber: 'JOHN-CRANE-HGS',
            name: 'Hydrogen Seal Assembly',
            description: 'Shaft seal assembly for hydrogen-cooled generator, includes seal ring and journal',
            category: 'generator_parts',
            manufacturer: 'John Crane',
            unitCost: 8500,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['generator'],
            failureModes: ['seal_wear', 'hydrogen_leak', 'oil_contamination'],
            averageLifespan: { value: 40000, unit: 'hours' },
            specifications: { type: 'Journal-type', sealGas: 'Hydrogen', maxPressure: '75 psig' },
          },
        ],
      },
      {
        id: 'wind_turbine_parts',
        name: 'Wind Turbine Components',
        description: 'Gearbox components, pitch bearings, yaw systems, and blade repair materials for utility-scale wind turbines',
        parts: [
          {
            id: 'wt_gearbox_planet_gear',
            partNumber: 'ZF-WG-PG2MW',
            name: 'Wind Turbine Planetary Gear Set',
            description: 'Planet gear set for 2MW wind turbine gearbox first planetary stage',
            category: 'wind_turbine_parts',
            manufacturer: 'ZF Wind Power',
            unitCost: 45000,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['wind_turbine'],
            failureModes: ['gear_tooth_wear', 'micropitting', 'bearing_race_damage'],
            averageLifespan: { value: 80000, unit: 'hours' },
            specifications: { stage: 'First planetary', ratio: '1:5.7', ratedTorque: '1800 kNm' },
          },
          {
            id: 'wt_pitch_bearing',
            partNumber: 'ROTHE-ERDE-PB3M',
            name: 'Blade Pitch Bearing',
            description: 'Four-point contact slewing bearing for blade pitch control, 3m diameter class',
            category: 'wind_turbine_parts',
            manufacturer: 'Rothe Erde',
            unitCost: 22000,
            currency: 'USD',
            leadTime: { value: 14, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['wind_turbine'],
            failureModes: ['bearing_wear', 'raceway_damage', 'bolt_fatigue'],
            averageLifespan: { value: 20, unit: 'years' },
            specifications: { type: 'Four-point contact', diameter: '3m', loadCapacity: '5000 kN' },
          },
          {
            id: 'wt_gearbox_oil',
            partNumber: 'CASTROL-OPTIGEAR-320',
            name: 'Wind Turbine Gearbox Oil',
            description: 'Synthetic gear oil for wind turbine gearbox, ISO VG 320, 208L drum',
            category: 'wind_turbine_parts',
            manufacturer: 'Castrol',
            unitCost: 1200,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['wind_turbine'],
            failureModes: ['lubrication_failure', 'gear_wear'],
            specifications: { viscosity: 'ISO VG 320', type: 'Full synthetic PAO', volume: '208L' },
          },
        ],
      },
      {
        id: 'solar_inverter_parts',
        name: 'Solar Inverter Components',
        description: 'IGBT modules, capacitors, cooling fans, and control boards for utility-scale solar inverters',
        parts: [
          {
            id: 'solar_igbt_module',
            partNumber: 'INFINEON-FF600R17',
            name: 'IGBT Power Module 1700V/600A',
            description: 'High-power IGBT module for central inverter power stage, 1700V 600A rating',
            category: 'solar_inverter_parts',
            manufacturer: 'Infineon',
            unitCost: 1800,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['solar_array'],
            failureModes: ['igbt_short', 'thermal_fatigue', 'gate_driver_failure'],
            averageLifespan: { value: 60000, unit: 'hours' },
            specifications: { voltage: '1700V', current: '600A', switching: '20kHz max' },
          },
          {
            id: 'solar_dc_bus_cap',
            partNumber: 'EPCOS-B25690',
            name: 'DC Bus Link Capacitor',
            description: 'Film capacitor for inverter DC bus filtering, 1100VDC 500uF',
            category: 'solar_inverter_parts',
            manufacturer: 'EPCOS/TDK',
            unitCost: 420,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['solar_array'],
            failureModes: ['capacitor_dry_out', 'esr_increase', 'dielectric_breakdown'],
            averageLifespan: { value: 50000, unit: 'hours' },
            specifications: { voltage: '1100VDC', capacitance: '500uF', type: 'Polypropylene film' },
          },
          {
            id: 'solar_cooling_fan',
            partNumber: 'EBM-W2E200',
            name: 'Inverter Cooling Fan Assembly',
            description: 'AC axial fan for inverter cabinet forced-air cooling, 200mm',
            category: 'solar_inverter_parts',
            manufacturer: 'ebm-papst',
            unitCost: 185,
            currency: 'USD',
            leadTime: { value: 1, unit: 'weeks' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['solar_array', 'battery_storage'],
            failureModes: ['fan_bearing_failure', 'overheating'],
            averageLifespan: { value: 40000, unit: 'hours' },
            specifications: { size: '200mm', airflow: '935 m3/h', voltage: '230VAC' },
          },
        ],
      },
      {
        id: 'battery_storage_parts',
        name: 'Battery Storage Components',
        description: 'BMS modules, contactors, thermal management components for grid-scale battery systems',
        parts: [
          {
            id: 'bess_bms_module',
            partNumber: 'NUVATION-BMS-HC',
            name: 'Battery Management System Module',
            description: 'High-channel BMS slave module for cell-level voltage and temperature monitoring',
            category: 'battery_storage_parts',
            manufacturer: 'Nuvation Energy',
            unitCost: 650,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['battery_storage'],
            failureModes: ['bms_communication_loss', 'sensor_failure', 'cell_imbalance'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { channels: '24 per module', accuracy: '±2mV', communication: 'CAN bus' },
          },
          {
            id: 'bess_dc_contactor',
            partNumber: 'GIGAVAC-GX26',
            name: 'High-Voltage DC Contactor',
            description: 'Hermetically sealed DC contactor for battery string switching, 1000VDC 500A',
            category: 'battery_storage_parts',
            manufacturer: 'Gigavac',
            unitCost: 920,
            currency: 'USD',
            leadTime: { value: 3, unit: 'weeks' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['battery_storage'],
            failureModes: ['contact_welding', 'coil_failure', 'arc_damage'],
            averageLifespan: { value: 100000, unit: 'cycles' },
            specifications: { voltage: '1000VDC', current: '500A', type: 'Hermetically sealed' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Energy Industry)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'electrical_failures',
        name: 'Electrical Failures',
        description: 'Root causes related to electrical insulation, dielectric breakdown, and power system faults',
        icon: 'Zap',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'insulation_breakdown',
            name: 'Insulation Breakdown',
            description: 'Progressive degradation of electrical insulation in transformers, generators, or cables leading to dielectric failure',
            category: 'electrical_failures',
            symptoms: [
              'Partial discharge activity detected',
              'Elevated dissolved gas levels in oil',
              'Reduced insulation resistance',
              'Increased tan-delta values',
              'Localized hot spots on thermography',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform dissolved gas analysis (DGA) on transformer oil',
                expectedResult: 'Identify fault gas patterns (hydrogen, acetylene, ethylene)',
                tools: ['DGA sampling kit', 'Gas chromatograph'],
                duration: 120,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Conduct insulation resistance and polarization index test',
                expectedResult: 'IR > 1 GΩ, PI > 2.0 for healthy insulation',
                tools: ['Megohmmeter (5kV)'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Perform partial discharge measurement',
                expectedResult: 'PD levels below 500 pC for transformer, <1000 pC for generator',
                tools: ['PD detector', 'Coupling capacitor'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 4,
                action: 'Infrared thermography scan of accessible components',
                expectedResult: 'No hot spots exceeding 10°C above ambient reference',
                tools: ['IR thermal camera'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Regular DGA testing on schedule (6-12 months)',
              'Monitor oil quality and moisture content',
              'Perform periodic insulation resistance trending',
              'Maintain proper cooling system operation',
              'Avoid overloading beyond nameplate ratings',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['transformer_windings', 'bushings', 'generator_stator', 'cables'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 8, typical: 72, max: 720, unit: 'hours' },
            costRange: { min: 5000, max: 2000000, currency: 'USD' },
            industryReferences: ['IEEE C57.104', 'IEEE 43', 'IEC 60599'],
          },
          {
            id: 'bushing_failure',
            name: 'Transformer Bushing Failure',
            description: 'Degradation or catastrophic failure of high-voltage bushings due to moisture ingress, oil contamination, or partial discharge',
            category: 'electrical_failures',
            symptoms: [
              'Increased power factor / tan-delta',
              'Oil leakage at bushing flange',
              'Elevated C1 capacitance change',
              'Partial discharge detected',
              'Discolored oil in bushing sight glass',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform power factor (tan-delta) test on bushing',
                expectedResult: 'Power factor < 0.5% for condenser bushings',
                tools: ['Doble test set', 'Power factor bridge'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Measure bushing capacitance (C1 and C2)',
                expectedResult: 'Capacitance within ±5% of nameplate value',
                tools: ['Capacitance bridge'],
                duration: 30,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Visual inspection for oil leaks and surface condition',
                expectedResult: 'No cracks, no oil seepage, clean porcelain',
                tools: ['Binoculars', 'Flashlight'],
                duration: 20,
                skillLevel: 'basic',
              },
            ],
            preventiveActions: [
              'Annual power factor testing',
              'Monitor bushing oil level',
              'Inspect gaskets and seals during outages',
              'Track capacitance trends over time',
              'Replace bushings exceeding recommended service life',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['bushings', 'transformer_tank', 'connections'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' },
            costRange: { min: 20000, max: 150000, currency: 'USD' },
            industryReferences: ['IEEE C57.19.01', 'IEC 60137'],
          },
        ],
      },
      {
        id: 'mechanical_degradation',
        name: 'Mechanical Degradation',
        description: 'Root causes related to wear, fatigue, and mechanical component deterioration',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'bearing_degradation',
            name: 'Bearing Degradation',
            description: 'Progressive wear of journal or rolling-element bearings in turbines, generators, or wind turbines leading to vibration and potential seizure',
            category: 'mechanical_degradation',
            symptoms: [
              'Increased vibration at bearing characteristic frequencies',
              'Elevated bearing temperature',
              'Metal particles in oil analysis',
              'Audible rumbling or grinding',
              'Shaft displacement drift',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform vibration spectrum analysis focusing on bearing defect frequencies',
                expectedResult: 'Identify BPFO, BPFI, BSF, or FTF peaks above baseline',
                tools: ['Vibration analyzer', 'Accelerometer'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Analyze lubricant sample for wear metals and particle count',
                expectedResult: 'Iron < 50 ppm, no large particles in patch test',
                tools: ['Oil sampling kit', 'Spectrometric oil analysis'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Measure shaft proximity probe readings (for journal bearings)',
                expectedResult: 'Shaft position within normal operating range, orbit circular',
                tools: ['Proximity probe monitor', 'Bently Nevada system'],
                duration: 15,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Implement continuous vibration monitoring with alert thresholds',
              'Follow strict oil analysis schedule',
              'Maintain proper oil temperature and flow',
              'Ensure correct shaft alignment',
              'Track bearing temperature trends',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['bearings', 'shafts', 'seals', 'gearbox'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 8, typical: 48, max: 168, unit: 'hours' },
            costRange: { min: 5000, max: 200000, currency: 'USD' },
            industryReferences: ['ISO 10816', 'ISO 7919', 'API 670'],
          },
          {
            id: 'blade_erosion',
            name: 'Turbine Blade Erosion/Damage',
            description: 'Surface erosion, cracking, or foreign object damage to gas turbine or wind turbine blades reducing efficiency and risking catastrophic failure',
            category: 'mechanical_degradation',
            symptoms: [
              'Decreased power output at same conditions',
              'Increased exhaust temperature spread',
              'Elevated vibration at blade-pass frequency',
              'Visual surface damage during borescope inspection',
              'Increased heat rate',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform borescope inspection of gas path (GT) or drone inspection (WT)',
                expectedResult: 'Identify erosion patterns, cracks, coating loss, or FOD damage',
                tools: ['Borescope', 'Inspection drone', 'Camera'],
                duration: 240,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Review exhaust temperature spread data',
                expectedResult: 'Spread < 30°F across thermocouples for gas turbines',
                tools: ['DCS/SCADA historian'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Analyze performance degradation trends',
                expectedResult: 'Power output and heat rate trending within expected degradation curve',
                tools: ['Performance monitoring system'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Schedule regular borescope or drone inspections',
              'Install inlet air filtration (gas turbines)',
              'Monitor performance degradation trends',
              'Apply protective coatings during overhauls',
              'Track leading-edge erosion rates (wind turbines)',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['blades', 'vanes', 'shrouds', 'leading_edge'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 48, typical: 168, max: 720, unit: 'hours' },
            costRange: { min: 25000, max: 1500000, currency: 'USD' },
            industryReferences: ['GE TIL', 'IEC 61400-24'],
          },
        ],
      },
      {
        id: 'thermal_overload',
        name: 'Thermal Overload',
        description: 'Root causes related to excessive heat, thermal cycling, and cooling system failures',
        icon: 'Thermometer',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'thermal_fatigue',
            name: 'Thermal Fatigue',
            description: 'Material degradation from repeated thermal cycling during startups, load changes, and shutdowns',
            category: 'thermal_overload',
            symptoms: [
              'Surface cracking on hot-gas-path components',
              'Deformation of combustor liners',
              'Efficiency loss over time',
              'Increased exhaust temperature spread',
              'Creep elongation in blades',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform borescope inspection of combustor and hot gas path',
                expectedResult: 'Identify thermal stress cracks, oxidation, or creep deformation',
                tools: ['Borescope', 'Thermal camera'],
                duration: 180,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Review startup/shutdown cycle count and ramp rate history',
                expectedResult: 'Compare actual cycles to component life limits',
                tools: ['DCS historian', 'OEM life tracking tool'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Measure critical component dimensions against baseline',
                expectedResult: 'Creep elongation within allowable limits',
                tools: ['Precision measuring tools', 'CMM'],
                duration: 120,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Optimize dispatch to minimize cycling',
              'Control ramp rates during startup and shutdown',
              'Track equivalent operating hours and starts',
              'Schedule overhauls based on actual thermal duty',
              'Upgrade to advanced thermal barrier coatings',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['blades', 'combustor', 'transition_pieces', 'nozzles'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 24, typical: 72, max: 168, unit: 'hours' },
            costRange: { min: 50000, max: 500000, currency: 'USD' },
            industryReferences: ['GE GEK-116571', 'EPRI Report 1010477'],
          },
          {
            id: 'cooling_system_failure',
            name: 'Cooling System Failure',
            description: 'Failure of cooling systems in transformers, generators, or inverters leading to overheating and accelerated aging',
            category: 'thermal_overload',
            symptoms: [
              'Rising oil or winding temperature alarms',
              'Cooling fan or pump failure alerts',
              'Forced power derating',
              'Accelerated insulation aging indicators',
              'High top-oil temperature',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Verify cooling fan and pump operation',
                expectedResult: 'All cooling stages operating, correct rotation, adequate flow',
                tools: ['Ammeter', 'Flow meter', 'Visual inspection'],
                duration: 30,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Check radiator/cooler condition for blockage or fouling',
                expectedResult: 'Clean heat exchange surfaces, no debris or corrosion buildup',
                tools: ['Flashlight', 'Thermal camera'],
                duration: 30,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Measure temperature differential across cooler',
                expectedResult: 'Temperature drop consistent with design heat rejection capacity',
                tools: ['Temperature probes', 'IR thermometer'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Routine cooling system inspection and cleaning',
              'Test cooling fan motors and controls periodically',
              'Monitor oil flow rates and temperatures',
              'Maintain cooling water treatment (if applicable)',
              'Verify automatic cooling stage controls operate correctly',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['cooling_fans', 'oil_pumps', 'radiators', 'heat_exchangers'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 2, typical: 8, max: 48, unit: 'hours' },
            costRange: { min: 500, max: 25000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'environmental_factors',
        name: 'Environmental Factors',
        description: 'Root causes related to weather, contamination, corrosion, and external environmental conditions',
        icon: 'Cloud',
        color: '#06b6d4',
        rootCauses: [
          {
            id: 'lightning_damage',
            name: 'Lightning Strike Damage',
            description: 'Direct or indirect lightning damage to transmission lines, transformers, wind turbines, or solar arrays',
            category: 'environmental_factors',
            symptoms: [
              'Sudden equipment trip or lockout',
              'Surge arrester failure or activation',
              'Control system reset or damage',
              'Visible arc damage or burn marks',
              'Instrument or sensor failures',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inspect surge arresters and grounding system',
                expectedResult: 'Identify failed arresters, damaged ground connections',
                tools: ['Ground resistance tester', 'Visual inspection'],
                duration: 60,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Test protection relays and control systems',
                expectedResult: 'All protection devices functioning correctly after event',
                tools: ['Relay test set', 'Multimeter'],
                duration: 120,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Review lightning detection data and event logs',
                expectedResult: 'Correlate equipment trip time with lightning strike data',
                tools: ['Lightning location system', 'SCADA event logs'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Install and maintain proper surge protection',
              'Verify grounding system integrity annually',
              'Install lightning protection systems on wind turbines',
              'Use shielded cables and proper cable routing',
              'Implement lightning detection and warning systems',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['surge_arresters', 'control_systems', 'transformers', 'wind_turbine_blades'],
            requiredExpertise: ['sme', 'technician'],
            estimatedResolutionTime: { min: 4, typical: 24, max: 168, unit: 'hours' },
            costRange: { min: 2000, max: 500000, currency: 'USD' },
            industryReferences: ['IEEE C62.22', 'IEC 62305', 'IEC 61400-24'],
          },
          {
            id: 'corrosion_degradation',
            name: 'Corrosion and Environmental Degradation',
            description: 'Corrosion, salt spray, UV degradation, or moisture ingress affecting outdoor equipment longevity',
            category: 'environmental_factors',
            symptoms: [
              'Visible rust or corrosion on structures and enclosures',
              'Degraded cable insulation or connector corrosion',
              'Reduced panel output from soiling or UV yellowing',
              'Ground resistance increasing over time',
              'Fastener degradation on tower structures',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection of structures, enclosures, and connections',
                expectedResult: 'Document corrosion extent, severity, and locations',
                tools: ['Camera', 'Pit gauge', 'Flashlight'],
                duration: 120,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Measure ground resistance at equipment bases',
                expectedResult: 'Ground resistance < 5 ohms for substation equipment',
                tools: ['Ground resistance tester'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Inspect bolted connections and measure contact resistance',
                expectedResult: 'Contact resistance stable and within normal range',
                tools: ['Micro-ohmmeter', 'Torque wrench'],
                duration: 60,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Apply corrosion-resistant coatings and sealants',
              'Schedule regular washing of solar panels',
              'Implement cathodic protection where applicable',
              'Use stainless steel or galvanized hardware in corrosive environments',
              'Monitor ground resistance trends',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['structures', 'enclosures', 'connections', 'solar_panels', 'cables'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 4, typical: 24, max: 120, unit: 'hours' },
            costRange: { min: 1000, max: 50000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'protection_system_failures',
        name: 'Protection System Failures',
        description: 'Root causes related to relay misoperation, breaker failures, and protection scheme deficiencies',
        icon: 'Shield',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'relay_misoperation',
            name: 'Protective Relay Misoperation',
            description: 'Incorrect relay tripping (false trip) or failure to trip during a fault, potentially causing equipment damage or cascading outages',
            category: 'protection_system_failures',
            symptoms: [
              'Unexpected breaker trips during normal operation',
              'Failure to clear faults within coordination time',
              'Relay communication alarms',
              'Protection scheme coordination violations',
              'Cascading trip events',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Download and analyze relay event records and oscillography',
                expectedResult: 'Identify fault type, magnitude, duration, and relay response',
                tools: ['Relay software', 'Laptop'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Verify relay settings against coordination study',
                expectedResult: 'Settings match approved protection coordination study',
                tools: ['Protection coordination software', 'Settings database'],
                duration: 120,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Perform relay functional testing with injection',
                expectedResult: 'Relay operates correctly at specified pickup and time values',
                tools: ['Relay test set (Omicron, Doble)'],
                duration: 180,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Perform periodic relay testing per NERC PRC-005',
              'Keep relay firmware updated',
              'Review and update protection coordination studies',
              'Monitor relay self-diagnostics remotely',
              'Maintain accurate relay settings database',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['protective_relays', 'cts', 'pts', 'breakers', 'communication_channels'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 4, typical: 16, max: 72, unit: 'hours' },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['NERC PRC-005', 'IEEE C37.90', 'NERC Misoperation Reports'],
          },
          {
            id: 'breaker_failure',
            name: 'Circuit Breaker Failure',
            description: 'Failure of high-voltage circuit breaker to open or close on command due to mechanical, hydraulic, or control issues',
            category: 'protection_system_failures',
            symptoms: [
              'Breaker fails to trip on command',
              'Slow operating time exceeding specifications',
              'SF6 gas pressure low alarm',
              'Spring or hydraulic mechanism pressure loss',
              'Contact resistance increasing over time',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform breaker timing test (open and close)',
                expectedResult: 'Operating times within manufacturer specifications (typically <50ms)',
                tools: ['Breaker timing analyzer'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Measure contact resistance across each phase',
                expectedResult: 'Contact resistance < 100 micro-ohms, balanced across phases',
                tools: ['Micro-ohmmeter (DLRO)'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Check SF6 gas pressure and perform moisture analysis',
                expectedResult: 'Pressure at rated value, moisture < 250 ppm',
                tools: ['SF6 analyzer', 'Dew-point meter'],
                duration: 45,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Perform periodic breaker timing and contact resistance tests',
              'Monitor SF6 gas pressure continuously',
              'Lubricate mechanisms per manufacturer schedule',
              'Track operations counter against rated mechanical life',
              'Implement breaker failure protection scheme (BFP)',
            ],
            typicalProbability: 0.05,
            affectedComponents: ['breaker_contacts', 'operating_mechanism', 'sf6_gas_system', 'trip_coils'],
            requiredExpertise: ['sme', 'technician'],
            estimatedResolutionTime: { min: 8, typical: 24, max: 120, unit: 'hours' },
            costRange: { min: 5000, max: 300000, currency: 'USD' },
            industryReferences: ['IEEE C37.10', 'IEC 62271-100', 'CIGRE Technical Brochures'],
          },
        ],
      },
      {
        id: 'control_system_faults',
        name: 'Control System Faults',
        description: 'Root causes related to SCADA, DCS, PLC, and automation system failures affecting plant operations',
        icon: 'Cpu',
        color: '#10b981',
        rootCauses: [
          {
            id: 'scada_communication_loss',
            name: 'SCADA/Communication Loss',
            description: 'Loss of supervisory control and data acquisition communications resulting in blind operation and inability to remotely control assets',
            category: 'control_system_faults',
            symptoms: [
              'Data quality bad flags on operator displays',
              'Loss of remote control capability',
              'Communication alarm from RTUs or IEDs',
              'Stale data on SCADA screens',
              'Automatic generation control (AGC) failure',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check communication link status and network diagnostics',
                expectedResult: 'Identify failed link, high latency, or packet loss',
                tools: ['Network analyzer', 'Ping utility', 'SCADA diagnostics'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Verify RTU/IED power supply and local operation',
                expectedResult: 'Device powered and operating in local mode',
                tools: ['Multimeter', 'Laptop with configuration tool'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Test communication path including routers, switches, and modems',
                expectedResult: 'Identify failed component in communication chain',
                tools: ['Cable tester', 'Network analyzer'],
                duration: 60,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement redundant communication paths',
              'Monitor communication health metrics continuously',
              'Perform periodic communication system testing',
              'Maintain network equipment firmware updates',
              'Ensure cybersecurity measures do not block critical traffic',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['scada_master', 'rtus', 'communication_links', 'network_switches', 'ieds'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 1, typical: 4, max: 24, unit: 'hours' },
            costRange: { min: 500, max: 20000, currency: 'USD' },
            industryReferences: ['NERC CIP Standards', 'IEEE 1815 (DNP3)', 'IEC 61850'],
          },
          {
            id: 'control_logic_error',
            name: 'Control Logic / Software Error',
            description: 'Errors in PLC, DCS, or turbine control system programming causing incorrect equipment response or unintended trips',
            category: 'control_system_faults',
            symptoms: [
              'Unexpected equipment trips not explained by process conditions',
              'Incorrect valve or actuator response',
              'Setpoint errors or hunting',
              'Alarms that do not match field conditions',
              'Intermittent faults at specific operating conditions',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review alarm and event log chronology around the incident',
                expectedResult: 'Identify sequence of events and control actions taken',
                tools: ['DCS/PLC historian', 'Event viewer'],
                duration: 30,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Compare running control logic against approved baseline',
                expectedResult: 'Identify any unauthorized or unintended logic changes',
                tools: ['PLC programming software', 'Version control system'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Simulate fault conditions in test environment',
                expectedResult: 'Reproduce the failure to confirm root cause',
                tools: ['Simulation environment', 'Logic analyzer'],
                duration: 120,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Implement rigorous management-of-change for control logic',
              'Maintain version control for all control programs',
              'Perform factory acceptance testing for logic changes',
              'Use simulation testing before deploying changes to production',
              'Conduct periodic control system audits',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['plc', 'dcs', 'turbine_controller', 'bms'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 2, typical: 16, max: 72, unit: 'hours' },
            costRange: { min: 1000, max: 50000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'startup_stress',
        name: 'Startup/Cycling Stress Pattern',
        description: 'Accelerated degradation from frequent startups and shutdowns of thermal generation units',
        sequence: [
          'Frequent cycling or peaking dispatch',
          'Rapid temperature transients',
          'Thermal stress on hot-gas-path components',
          'Cracking and coating spallation',
          'Performance degradation',
          'Forced outage for repair',
        ],
        indicators: [
          'High startup/shutdown count relative to operating hours',
          'Increasing exhaust temperature spread',
          'Heat rate trending upward',
        ],
        preventionStrategy: 'Optimize dispatch to minimize cycling, use controlled ramp rates, track equivalent starts vs. OEM maintenance intervals',
      },
      {
        id: 'transformer_aging_cascade',
        name: 'Transformer Insulation Aging Cascade',
        description: 'Progressive transformer insulation degradation leading to eventual dielectric failure',
        sequence: [
          'Sustained overloading or inadequate cooling',
          'Elevated hot-spot temperatures',
          'Accelerated cellulose insulation aging',
          'Increased moisture and dissolved gas levels',
          'Partial discharge initiation',
          'Dielectric breakdown and failure',
        ],
        indicators: [
          'Rising furan levels in oil',
          'Increasing dissolved gas (especially CO, CO2)',
          'Decreasing insulation resistance over time',
        ],
        preventionStrategy: 'Regular DGA and furan analysis, maintain cooling systems, avoid sustained overloading, consider online DGA monitoring for critical units',
      },
      {
        id: 'wind_turbine_gearbox_cascade',
        name: 'Wind Turbine Gearbox Failure Cascade',
        description: 'Progressive gearbox degradation from bearing micropitting to catastrophic gear failure',
        sequence: [
          'Bearing micropitting from variable loads',
          'Metal particles in gearbox oil',
          'Accelerated gear tooth wear',
          'Vibration and noise increase',
          'Gear tooth fracture',
          'Gearbox seizure and turbine shutdown',
        ],
        indicators: [
          'Increasing iron content in oil samples',
          'Vibration spectrum changes at gear mesh frequencies',
          'Gearbox oil temperature rising above baseline',
        ],
        preventionStrategy: 'Implement continuous vibration and oil condition monitoring, use high-quality synthetic gear oils, schedule proactive gearbox endoscopy inspections',
      },
    ],
  },

  // Labor Rates
  laborRates: {
    roles: [
      { roleId: 'plant_operator', name: 'Plant Operator', hourlyRate: 45, currency: 'USD', certifications: ['NERC Certified'] },
      { roleId: 'turbine_tech', name: 'Turbine Technician', hourlyRate: 70, currency: 'USD', certifications: ['OEM Certified'] },
      { roleId: 'power_engineer', name: 'Power Systems Engineer', hourlyRate: 100, currency: 'USD', certifications: ['PE', 'NERC'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
